import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';

export function LoginPage() {
  const [pairingCode, setPairingCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleLogin = () => {
    window.location.href = '/api/v1/auth/google';
  };

  const handleMicrosoftLogin = () => {
    window.location.href = '/api/v1/auth/microsoft';
  };

  const handlePairingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pairingCode.length !== 6) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await api.post('/auth/pair/redeem', { code: pairingCode });
      // Server sets JWT cookie; redirect triggers authenticated session
      window.location.href = '/';
    } catch {
      setError('Invalid or expired pairing code');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img
            src="/my-family-calendar-primary-icon.png?v=2"
            alt="My Family Calendar"
            className="w-20 h-20 mx-auto mb-4 rounded-xl"
          />
          <h1 className="text-2xl font-semibold text-text-primary">My Family Calendar</h1>
          <p className="text-text-secondary mt-2">Your household command center</p>
        </div>

        {/* Login Card */}
        <div className="card">
          <h2 className="text-lg font-semibold text-text-primary mb-6 text-center">
            Sign in to continue
          </h2>

          <div className="space-y-3">
            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-border-default rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="text-text-primary font-medium">Continue with Google</span>
            </button>

            <button
              onClick={handleMicrosoftLogin}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-border-default rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#F25022" d="M1 1h10v10H1z" />
                <path fill="#00A4EF" d="M1 13h10v10H1z" />
                <path fill="#7FBA00" d="M13 1h10v10H13z" />
                <path fill="#FFB900" d="M13 13h10v10H13z" />
              </svg>
              <span className="text-text-primary font-medium">Continue with Microsoft</span>
            </button>
          </div>

          {/* Pairing Code Section - for kiosk devices unable to complete OAuth in WebView */}
          <div className="mt-6 pt-6 border-t border-border-default">
            <p className="text-sm text-text-secondary text-center mb-4">
              Have a pairing code from another device?
            </p>
            <form onSubmit={handlePairingSubmit} className="space-y-3">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={pairingCode}
                onChange={(e) => setPairingCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                // tracking-widest (0.1em): prevents misreading similar digits during entry
                className="w-full px-4 py-3 border border-border-default rounded-lg text-center text-2xl tracking-widest font-mono"
              />
              {error && (
                <p className="text-sm text-coral text-center">{error}</p>
              )}
              <button
                type="submit"
                disabled={pairingCode.length !== 6 || isSubmitting}
                className="w-full px-4 py-3 bg-soft-cyan text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Pairing...' : 'Pair Device'}
              </button>
            </form>
            <p className="text-xs text-text-muted text-center mt-3">
              Need a code? Sign in on another device and visit{' '}
              <span className="font-medium">/pair</span> to generate one.
            </p>
          </div>

          <p className="text-xs text-text-muted text-center mt-6">
            By signing in, you agree to our{' '}
            <Link to="/terms" className="text-blue hover:underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-blue hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        {/* Color Palette Preview */}
        <div className="mt-8 flex justify-center gap-2">
          <div className="w-8 h-8 rounded-full bg-teal" title="Teal" />
          <div className="w-8 h-8 rounded-full bg-sky" title="Sky" />
          <div className="w-8 h-8 rounded-full bg-amber" title="Amber" />
          <div className="w-8 h-8 rounded-full bg-orange" title="Orange" />
          <div className="w-8 h-8 rounded-full bg-navy" title="Navy" />
        </div>
      </div>
    </div>
  );
}
